import { useEffect, useCallback } from 'react';
import { RoomEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { isServerNoticesRoom } from '../utils/serverNoticesUtils';
import logger from '../utils/logger';

/**
 * Hook to auto-accept server notices room invites
 * 
 * This hook ensures that server notices rooms never appear as invites in the UI.
 * When a user receives their first server notice, the room is automatically joined
 * so it appears immediately in the channels list with the proper avatar.
 * 
 * This solves two issues:
 * 1. Server notices room appearing as invite (requires manual accept)
 * 2. Avatar not updating immediately (caused by invite→join timing)
 * 
 * Auto-accepting is safe because:
 * - Server notices rooms are controlled by the homeserver admin
 * - Users cannot be added to malicious rooms via this mechanism
 * - The room is identified by strict criteria (server user ID patterns)
 */
const useServerNoticesAutoJoin = () => {
  const client = useMatrixClient();

  const handleInvite = useCallback(
    async (room, membership) => {
      // Log all invites for debugging
      logger.debug(`[useServerNoticesAutoJoin] Membership change: roomId=${room?.roomId}, membership=${membership}`);

      // Only process invites
      if (membership !== 'invite') {
        return;
      }

      // Check if this is a server notices room
      const isServerNotices = isServerNoticesRoom(room, client);
      logger.debug(`[useServerNoticesAutoJoin] Is server notices room: ${isServerNotices}, roomId=${room?.roomId}`);

      if (!isServerNotices) {
        return;
      }

      const roomId = room?.roomId;
      if (!roomId) {
        logger.warn('[useServerNoticesAutoJoin] Room has no roomId, skipping auto-join');
        return;
      }

      logger.info(`[useServerNoticesAutoJoin] Auto-accepting server notices room invite: ${roomId}`);

      try {
        // Auto-join the server notices room
        await client.joinRoom(roomId);
        logger.info(`[useServerNoticesAutoJoin] Successfully auto-joined server notices room: ${roomId}`);
      } catch (error) {
        logger.error(`[useServerNoticesAutoJoin] Failed to auto-join server notices room ${roomId}:`, error);
        // Don't throw - let the user see the invite if auto-join fails
        // This provides a fallback mechanism
      }
    },
    [client],
  );

  useEffect(() => {
    if (!client) {
      return;
    }

    // Listen for membership changes on all rooms
    client.on(RoomEvent.MyMembership, handleInvite);

    return () => {
      client.off(RoomEvent.MyMembership, handleInvite);
    };
  }, [client, handleInvite]);
};

export default useServerNoticesAutoJoin;
